import { FilterItem } from 'app/shared/models/filter-group.models';
import { ActiveChannelDTO } from 'app/shared/models/channel.model';
import { Shop } from 'app/shared/models/shop.model';
import { styleGroupDTO } from 'app/shared/models/style-group.model';
import { union } from 'lodash';
import { BasicCollectionDTO } from '../models/common/basic-collection.models';
import { CustomerVM, GeneralFieldsDTO, GeneralFieldsVM } from '../models/customer/customer.model';
import { PointOfSaleDetailVM } from '../models/point-of-sale/bo-point-of-sale.model';
import { ShippingAddressVM } from '../models/point-of-sale/shipping-address.model';
import { AssortmentItemVM } from '../models/products/assortment.model';
import { ProductDTO, SizeDTO } from '../models/products/product.models';
import { VariantPaletteDTO } from '../models/products/variant.models';
import { TierVM } from '../models/settings/tier.model';
import { RoleVM } from '../models/users/role.models';
import { FilterIncompatibilityEnum } from 'app/shared/enums/filter-incompatibility.enum.model';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';
import { SizeGroupTypeEnum } from '../enum/size-group-type.enum.model';
import { AgencyListItemVM } from '../models/salesNetwork/agency.model';
import { UserVM } from '../models/users/user.model';
import { SeasonsReferenceDTO } from 'app/shared/models/seasons-reference/seasons-reference.model';
import { VoucherVM } from '../models/back-office-admin-common/vouchers.model';
import { CustomizationRequestDTO } from 'app/shared/models/customization-request.models';
import { Showcase } from 'app/shared/models/showcases.model';
import { LocationFullDTO } from 'app/shared/models/location.model';
import { OrderVM } from '../models/back-office-admin-common/order.model';

export class FilterMapper {


  public static AgencyFilter(agency: AgencyListItemVM, isOnlyName = false) {
    const list: FilterItem = new FilterItem({
      value: agency.id,
      text: isOnlyName ? agency.name : agency.fullName,
    });
    return list;
  }

  public static TierFilter(tier: TierVM) {
    const list: FilterItem = new FilterItem({
      value: tier.id,
      text: tier.name,
    });
    return list;
  }

  public static OnlineStoreFilter(shop: Shop) {
    const list: FilterItem = new FilterItem({
      value: shop.id,
      text: shop.name,
    });
    return list;
  }

  public static cityFilter(location: LocationFullDTO[]) {
    let cities: string[] = [];
    location.forEach(el => { if (el?.city) cities.push(el.city); });
    cities = union(cities);
    const list: FilterItem[] = [];
    cities.forEach(el => {
      list.push(new FilterItem({
        value: el,
        text: el,
      }));
    });
    return list;
  }

  public static CustomerFilter(customer: CustomerVM) {
    const list: FilterItem = new FilterItem({
      value: customer.id,
      text: customer.name + ' - ' + customer.vatNumber,
    });
    return list;
  }

  public static SeasonReferenceFilter(SR: SeasonsReferenceDTO) {
    const list: FilterItem = new FilterItem({
      value: SR.id,
      text: SR.name + ' - ' + SR.externalId,
    });
    return list;
  }

  public static CustomerGroupCodeFilter(CGC: GeneralFieldsDTO) {
    const list: FilterItem = new FilterItem({
      value: CGC.id,
      text: CGC.code,
    });
    return list;
  }

  public static CustomerSubGroupCodeFilter(CSG: GeneralFieldsDTO) {
    const list: FilterItem = new FilterItem({
      value: CSG.id,
      text: CSG.code,
    });
    return list;
  }

  public static CustomerSignBoardFilter(CSB: GeneralFieldsDTO) {
    const list: FilterItem = new FilterItem({
      value: CSB.id,
      text: CSB.code,
    });
    return list;
  }

  public static CustomerHoldingFilter(CH: GeneralFieldsDTO) {
    const list: FilterItem = new FilterItem({
      value: CH.id,
      text: CH.code,
    });
    return list;
  }

  public static CustomerGroupFilter(CG: GeneralFieldsDTO) {
    const list: FilterItem = new FilterItem({
      value: CG.id,
      text: CG.code,
    });
    return list;
  }

  public static SalesTaxGroupFilter(STG: GeneralFieldsDTO) {
    const list: FilterItem = new FilterItem({
      value: STG.id,
      text: STG.code,
    });
    return list;
  }

  public static CustomizationRequestFilter(CR: CustomizationRequestDTO) {
    const list: FilterItem = new FilterItem({
      value: CR.id,
      text: CR.title,
    });
    return list;
  }
  
  public static ShowcaseFilter(SC: Showcase) {
    const list: FilterItem = new FilterItem({
      value: SC.id,
      text: SC.name,
    });
    return list;
  }

  public static VoucherFilter(voucher: VoucherVM) {
    const list: FilterItem = new FilterItem({
      value: voucher.id,
      text: voucher.description + ' - ' + voucher.code,
    });
    return list;
  }

  public static PosFilter(pos: PointOfSaleDetailVM) {
    const list: FilterItem = new FilterItem({
      value: pos.id,
      text: pos.name + ' - ' + pos.location.address1 + ', ' + pos.location.city,
    });
    return list;
  }

  public static roleFilter(role: RoleVM) {
    const list: FilterItem = new FilterItem({
      value: role.id,
      text: role.name,
    });
    return list;
  }

  public static assortmentFilter(assortment: AssortmentItemVM) {
    const list: FilterItem = new FilterItem({
      value: assortment.id,
      text: assortment.title,
    });
    return list;
  }

  public static productFilter(product: ProductDTO) {
    const list: FilterItem = new FilterItem({
      value: product.id,
      text: `${product.code} - ${product.title}`,
    });
    return list;
  }

  public static sizeFilter(size: SizeDTO) {
    const list: FilterItem = new FilterItem({
      value: size.id,
      text: `${UtilityFunctions.capitalize(SizeGroupTypeEnum[size.group])} - ${size.name}`,
    });
    return list;
  }

  public static paletteFilter(palette: VariantPaletteDTO) {
    const list: FilterItem = new FilterItem({
      value: palette.id,
      text: `${palette.externalColorCode.toUpperCase()} - ${palette.description}`,
    });
    return list;
  }

  public static basicCollectionFilter(collection: BasicCollectionDTO, use4shopId: boolean) {
    const list: FilterItem = new FilterItem({
      value: use4shopId ? collection.id : collection.externalCollectionId,
      text: collection.externalCollectionId,
    });
    return list;
  }

  public static styleGroupFilter(styleGroup: styleGroupDTO) {
    const list: FilterItem = new FilterItem({
      value: styleGroup.id,
      text: styleGroup.name,
    });
    return list;
  }

  public static ChannelFilter(channel: ActiveChannelDTO) {
    const list: FilterItem = new FilterItem({
      value: channel.channel.id,
      text: channel.channel.name,
    });
    return list;
  }


  public static FilterIncompatibility(currentFilters) {
    const list: FilterIncompatibilityEnum[] = [];
    currentFilters.filter(x => x.filterIncompatibility?.length)?.forEach(x => {
      list.push(...x.filterIncompatibility);
    });
    return UtilityFunctions.RemoveDuplicateData(list);
  }

  public static UserFilter(user: UserVM) {
    const list: FilterItem = new FilterItem({
      value: user.id,
      text: user.name,
    });
    return list;
  }

  public static OrderFilter(order: OrderVM) {
    const list: FilterItem = new FilterItem({
      value: order.id,
      text: order.name,
    });
    return list;
  }

}
